import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import g1 from "@/assets/gallery/g1.jpeg.asset.json";
import g2 from "@/assets/gallery/g2.jpeg.asset.json";
import g3 from "@/assets/gallery/g3.jpeg.asset.json";
import g4 from "@/assets/gallery/g4.jpeg.asset.json";
import g5 from "@/assets/gallery/g5.jpeg.asset.json";
import g6 from "@/assets/gallery/g6.jpeg.asset.json";
import g7 from "@/assets/gallery/g7.jpeg.asset.json";
import g8 from "@/assets/gallery/g8.jpeg.asset.json";
import g9 from "@/assets/gallery/g9.jpeg.asset.json";
import g10 from "@/assets/gallery/g10.jpeg.asset.json";
import g11 from "@/assets/gallery/g11.jpeg.asset.json";
import g12 from "@/assets/gallery/g12.JPG.asset.json";
import g13 from "@/assets/gallery/g13.png.asset.json";
import g14 from "@/assets/gallery/g14.jpg.asset.json";
import g15 from "@/assets/gallery/g15.jpg.asset.json";
import g16 from "@/assets/gallery/g16.jpg.asset.json";
import g17 from "@/assets/gallery/g17.jpg.asset.json";
import g18 from "@/assets/gallery/g18.jpeg.asset.json";
import g19 from "@/assets/gallery/g19.jpg.asset.json";
import g20 from "@/assets/gallery/g20.jpg.asset.json";
import g21 from "@/assets/gallery/g21.jpg.asset.json";
import g22 from "@/assets/gallery/g22.jpg.asset.json";
import g23 from "@/assets/gallery/g23.jpg.asset.json";
import g24 from "@/assets/gallery/g24.jpg.asset.json";
import g25 from "@/assets/gallery/g25.jpg.asset.json";
import g26 from "@/assets/gallery/g26.jpg.asset.json";

export const Route = createFileRoute("/gallery")({
  head: () =>
    seoHead({
      path: "/gallery",
      title: "Gallery — Coffman Cove Lodging",
      description: "Guests, catches, and days on the water in Coffman Cove, Alaska.",
    }),
  component: Gallery,
});

const SHOTS = [
  { src: g9.url, alt: "Guest with halibut at the Coffman Cove harbor sign" },
  { src: g2.url, alt: "Guests with a Pacific halibut" },
  { src: g3.url, alt: "Guests celebrating a fresh King salmon" },
  { src: g12.url, alt: "Young angler holding a fresh King salmon on the boat" },
  { src: g5.url, alt: "Angler with a big halibut on the boat" },
  { src: g11.url, alt: "Guests posing with a giant halibut at the dock" },
  { src: g8.url, alt: "Young angler with a halibut on deck" },
  { src: g1.url, alt: "Day's catch of halibut on the dock" },
  { src: g10.url, alt: "Vacuum-packed fish fillets prepared for the trip home" },
  { src: g7.url, alt: "Limit of halibut laid out on the dock" },
  { src: g6.url, alt: "Full harbor cleaning station with halibut" },
  { src: g4.url, alt: "Guest cruising the inland passage" },
  { src: g13.url, alt: "Guests with halibut under the Welcome to Coffman Cove Alaska sign" },
  { src: g14.url, alt: "Group of guests behind a long rail of fresh salmon" },
  { src: g17.url, alt: "Angler kneeling next to a massive Pacific halibut on the dock" },
  { src: g20.url, alt: "Guest posing beside a giant halibut at the harbor" },
  { src: g18.url, alt: "Guests on the dock with the day's limit of salmon" },
  { src: g15.url, alt: "Guests fighting fish from the deck of a rental boat" },
  { src: g16.url, alt: "Group of guests inside The Coffman Cove Bunkhouse" },
  { src: g19.url, alt: "Two guests in front of the Coffman Cove Bunkhouse sign" },
  { src: g21.url, alt: "Guests posing with a huge halibut hung on the Coffman Cove dock" },
  { src: g26.url, alt: "Group under the Welcome to Coffman Cove Alaska sign with a full spread of halibut" },
  { src: g22.url, alt: "Father and son with a big spread of halibut and rockfish at the Coffman Cove sign" },
  { src: g23.url, alt: "Angler holding up a bright white-belly halibut on the boat" },
  { src: g25.url, alt: "Angler with a hefty King salmon" },
  { src: g24.url, alt: "Group behind a long rail of fresh salmon at the cleaning station" },
  { src: g4.url, alt: "Guest cruising the inland passage" },
];

function Gallery() {
  const [idx, setIdx] = useState<number | null>(null);
  const open = idx !== null;
  const close = useCallback(() => setIdx(null), []);
  const prev = useCallback(() => setIdx((i) => (i === null ? i : (i - 1 + SHOTS.length) % SHOTS.length)), []);
  const next = useCallback(() => setIdx((i) => (i === null ? i : (i + 1) % SHOTS.length)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close, prev, next]);

  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <p className="uppercase tracking-[0.3em] text-xs text-white/80">Gallery</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 text-balance text-white">
            Days on the water.
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Guests, rental boats, and the catches that keep them coming back to Coffman Cove.
          </p>
          <Link
            to="/submit-photo"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
          >
            <Upload className="h-4 w-4" /> Upload your photo
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground max-w-xl">
            Got a shot from your trip? Send it in — the Hedges family reviews every
            submission before it joins the gallery.
          </p>
          <Link
            to="/submit-photo"
            className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-muted"
          >
            <Upload className="h-4 w-4" /> Submit a photo
          </Link>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24 pt-10 columns-1 sm:columns-2 lg:columns-3 gap-4">
        {SHOTS.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIdx(i)}
            className="mb-4 block w-full break-inside-avoid overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            aria-label={`Open photo: ${s.alt}`}
          >
            <img
              src={s.src}
              alt={s.alt}
              loading="lazy"
              className="w-full rounded-lg object-cover transition-transform duration-300 hover:scale-[1.02]"
            />
          </button>
        ))}
      </section>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous photo"
            className="absolute left-2 sm:left-4 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next photo"
            className="absolute right-2 sm:right-4 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <figure
            className="mx-auto flex max-h-[92vh] max-w-[95vw] flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={SHOTS[idx!].src}
              alt={SHOTS[idx!].alt}
              className="max-h-[85vh] max-w-[95vw] rounded-lg object-contain"
            />
            <figcaption className="text-center text-sm text-white/80">
              {SHOTS[idx!].alt} · {idx! + 1} / {SHOTS.length}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
